import React from "react";
import { Box, FormControlLabel, InputLabel, Switch } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Controller, useFormContext } from "react-hook-form";
import moment from "moment";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { OfferFormData } from "../../models/models";
import { PublicServiceRequest } from "../../../serviceBoard/models/models";
import AvailableDatePicker from "../scheduling/AvailableDatePicker";
import { dt } from "../../../../shared/themes/redesignTokens";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

interface StepSchedulingProps {
  fullServiceRequest?: PublicServiceRequest;
}

const StepScheduling: React.FC<StepSchedulingProps> = ({ fullServiceRequest }) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const { control, watch, setValue } = useFormContext<OfferFormData>();
  const immediateStart = watch("immediateStart");
  const availableDates = watch("availableDates") || [];

  const hasPreferredDates = fullServiceRequest && (fullServiceRequest.serviceStartDate || fullServiceRequest.serviceEndDate);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Section title */}
      <Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "20px", fontWeight: 700, color: dt.textPrimary, mb: 0.5 }}>
          {translate("schedulingTitle")}
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textMuted }}>
          {translate("schedulingHelperText")}
        </Box>
      </Box>

      {/* Customer's preferred dates info box */}
      {hasPreferredDates && (
        <Box
          sx={{
            background: "#EFF6FF",
            borderLeft: "4px solid #3B82F6",
            borderRadius: dt.radiusSm,
            p: 2,
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
          }}
        >
          <CalendarTodayOutlinedIcon sx={{ fontSize: 20, color: "#3B82F6", mt: 0.25, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 600, color: "#1E40AF", mb: 0.75 }}>
              {translate("createOffer.customerPreferredDates")}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 0.5 }}>
              {fullServiceRequest.serviceStartDate && (
                <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: "#1E3A5F" }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {translate("createOffer.preferredStart")}:
                  </Box>{" "}
                  {formatUiDate(fullServiceRequest.serviceStartDate)}
                </Box>
              )}
              {fullServiceRequest.serviceEndDate && (
                <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: "#1E3A5F" }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {translate("createOffer.preferredEnd")}:
                  </Box>{" "}
                  {formatUiDate(fullServiceRequest.serviceEndDate)}
                </Box>
              )}
            </Box>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", color: "#6B7280", fontStyle: "italic" }}>
              {fullServiceRequest.flexibleDates
                ? translate("createOffer.datesFlexible")
                : translate("createOffer.datesFixed")}
            </Box>
          </Box>
        </Box>
      )}

      {/* Immediate Start toggle */}
      <Box
        sx={{
          background: dt.cardBg,
          borderRadius: dt.radius,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          p: 2.5,
        }}
      >
        <Controller
          control={control}
          name="immediateStart"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: dt.blue },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: dt.blue },
                  }}
                />
              }
              label={
                <Box>
                  <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 600, color: dt.textPrimary }}>
                    {translate("immediateStartLabel")}
                  </Box>
                  <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted }}>
                    {translate("immediateStartHelper")}
                  </Box>
                </Box>
              }
              sx={{ alignItems: "flex-start", ml: 0, gap: 1.5 }}
            />
          )}
        />
      </Box>

      {/* Earliest Start Date */}
      {!immediateStart && (
        <Box
          sx={{
            background: dt.cardBg,
            borderRadius: dt.radius,
            border: `1px solid ${dt.cardBorder}`,
            boxShadow: dt.cardShadow,
            p: 2.5,
          }}
        >
          <InputLabel
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              color: dt.textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              mb: 1.5,
            }}
          >
            {translate("earliestStartDate")}
          </InputLabel>
          <Controller
            control={control}
            name="earliestStartDate"
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  {...field}
                  value={field.value ? moment(field.value) : null}
                  onChange={(value) => field.onChange(value?.toDate())}
                  minDate={moment()}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        maxWidth: "280px",
                        "& .MuiOutlinedInput-root": {
                          fontFamily: dt.font,
                          fontSize: "13px",
                          borderRadius: dt.radiusSm,
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Box>
      )}

      {/* Available Dates (multi-date picker) */}
      <Box
        sx={{
          background: dt.cardBg,
          borderRadius: dt.radius,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          p: 2.5,
        }}
      >
        <InputLabel
          sx={{
            fontFamily: dt.font,
            fontSize: "12px",
            fontWeight: 600,
            color: dt.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 0.5,
          }}
        >
          {translate("availableDatesLabel")}
        </InputLabel>
        <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted, mb: 1.5 }}>
          {translate("availableDatesHelper")}
        </Box>
        <AvailableDatePicker
          dates={availableDates}
          onChange={(dates) => setValue("availableDates", dates)}
        />
      </Box>
    </Box>
  );
};

export default StepScheduling;
